import { useEffect, useId, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence } from "motion/react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackBmiDisqualified } from "@/features/analytics/trackers/bmi-disqualified";
import { trackBmiPageViewed } from "@/features/analytics/trackers/bmi-page-viewed";
import { trackDqErrorShown } from "@/features/analytics/trackers/dq-error-shown";
import { trackBmiGroup } from "@/features/analytics/trackers/patient-profile-captured";
import { useSubmitBMIWithCategories } from "@/features/onboarding/hooks";
import type { CategoryEligibility } from "@/features/onboarding/models";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import {
  ImperialFields,
  MetricFields,
  PartialDisqualificationModal,
  UnitSystemSelector,
} from "@/features/onboarding/sections";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { GLP1_CATEGORIES } from "@/features/products/models";
import { LogoLoader } from "@/shared/components";
import { CATEGORY_SLUG } from "@/shared/domains";
import { useBoolean } from "@/shared/hooks";
import {
  AVAILABLE_LANGUAGE,
  bmiFormSchema,
  UNIT_SYSTEM,
} from "@/shared/models";
import { useSettingsStore } from "@/shared/stores";
import {
  errorToast,
  Form,
  Heading,
  Layout,
  SectionHeader,
  Text,
} from "@/shared/ui";
import { calculateBmi, convertMetricToImperialBmiInputs } from "@/shared/utils";

export const BmiCalculation = () => {
  const formId = useId();

  const { analytics } = useCustomerIoContext();

  const language = useSettingsStore((state) => state.language);
  const bmiInputs = useOnboardingStore((state) => state.bmiInputs);
  const setBmiInputs = useOnboardingStore((state) => state.setBmiInputs);
  const setSelectedCategories = useOnboardingStore(
    (state) => state.setSelectedCategories,
  );
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(bmiFormSchema),
    defaultValues: {
      unitSystem: UNIT_SYSTEM.IMPERIAL,
      ...bmiInputs,
    },
  });

  const submitBmiWithCategories = useSubmitBMIWithCategories();

  useEffect(() => {
    trackBmiPageViewed({ customerIoAnalytics: analytics });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    value: showPartialDisqualification,
    setTrue: openPartialDisqualification,
    setFalse: closePartialDisqualification,
  } = useBoolean(false);

  const handleNavigationBasedOnEligibility = (
    bmiCategoriesData: CategoryEligibility[],
  ) => {
    const hasEligibilityQuestions = bmiCategoriesData.some(
      (item) => item.hasEligibilityQuestions,
    );

    navigate(
      hasEligibilityQuestions
        ? ONBOARDING_ROUTE.ELIGIBILITY_QUESTIONS
        : ONBOARDING_ROUTE.ACCOUNT_PERSONAL_DETAILS,
    );
  };

  const handleSubmit = form.handleSubmit((value) => {
    setErrorMessage("");

    const bmiInputs =
      value.unitSystem === UNIT_SYSTEM.METRIC
        ? convertMetricToImperialBmiInputs({
            heightInCentimeters: value.heightInCentimeters,
            weightInKilograms: value.weightInKilograms,
          })
        : {
            heightFeet: value.heightFeet,
            heightInches: value.heightInches,
            weightInPounds: value.weightInPounds,
          };

    setBmiInputs(bmiInputs);

    const payload = {
      ...bmiInputs,
      bmi: calculateBmi(bmiInputs),
      selectedCategories: [CATEGORY_SLUG.WEIGHT_LOSS],
    };

    trackBmiGroup(payload.bmi);

    submitBmiWithCategories.mutate(payload, {
      onSuccess: (data) => {
        const categories = data.map((item) => item.category);
        setSelectedCategories(categories);

        const allCategoriesRequireBmi = data.every((item) =>
          GLP1_CATEGORIES.includes(item.category.slug),
        );

        const bmiDisqualifiedCategorySlug = data.find(
          (item) =>
            item.disqualified && GLP1_CATEGORIES.includes(item.category.slug),
        );

        if (bmiDisqualifiedCategorySlug && allCategoriesRequireBmi) {
          trackBmiDisqualified({ customerIoAnalytics: analytics });
          trackDqErrorShown("bmi");
          return setErrorMessage(
            t`Your BMI does not meet the minimum requirement for our weight loss program. Please edit your selection to continue.`,
          );
        }

        if (bmiDisqualifiedCategorySlug) {
          return openPartialDisqualification();
        }

        handleNavigationBasedOnEligibility(data);
      },
      onError: errorToast,
    });
  });

  const handleDisqualificationContinue = () => {
    const bmiCategories = submitBmiWithCategories.data;

    if (!bmiCategories) {
      return navigate(ONBOARDING_ROUTE.ACCOUNT_PERSONAL_DETAILS);
    }

    const nonGlp1Categories = bmiCategories
      .filter((item) => !GLP1_CATEGORIES.includes(item.category.slug))
      .map((item) => item.category);

    setSelectedCategories(nonGlp1Categories);
    handleNavigationBasedOnEligibility(
      bmiCategories.filter(
        (item) => !GLP1_CATEGORIES.includes(item.category.slug),
      ),
    );
  };

  const disqualifiedCategorySlug = submitBmiWithCategories.data?.find(
    (item) => item.disqualified && GLP1_CATEGORIES.includes(item.category.slug),
  )?.category.slug;

  const selectedUnitSystem = form.watch("unitSystem");

  return (
    <>
      <LogoLoader show={submitBmiWithCategories.isPending} fullscreen />

      <Layout.Content size="sm">
        <SectionHeader
          title={
            <>
              {t`Reach your goal weight`} <br />
              <span className="text-secondary-500">
                {t`without restrictive diets and exercise.`}
              </span>
            </>
          }
          description={t`Let's calculate your BMI to make sure you're a good candidate for our clinical program.`}
        />

        <Form.Provider {...form}>
          <form
            id={formId}
            onSubmit={handleSubmit}
            className="flex w-full flex-col gap-2"
          >
            <div className="flex w-full flex-col gap-3 sm:gap-7">
              <Heading level={2} color="primary" align="center">
                {t`What is your height and weight?`}
              </Heading>

              {language !== AVAILABLE_LANGUAGE.EN && (
                <UnitSystemSelector form={form} />
              )}

              <AnimatePresence mode="wait" initial={false}>
                {selectedUnitSystem === UNIT_SYSTEM.IMPERIAL && (
                  <ImperialFields
                    key="imperial-fields"
                    control={form.control}
                  />
                )}

                {selectedUnitSystem === UNIT_SYSTEM.METRIC && (
                  <MetricFields key="metric-fields" control={form.control} />
                )}
              </AnimatePresence>
            </div>

            <div className="flex flex-col gap-3">
              {errorMessage && (
                <Text align="center" color="error" as="p">
                  {errorMessage}
                </Text>
              )}

              <Layout.Action
                type="submit"
                form={formId}
              >{t`Continue`}</Layout.Action>
            </div>
          </form>
        </Form.Provider>
      </Layout.Content>

      {disqualifiedCategorySlug && (
        <PartialDisqualificationModal
          open={showPartialDisqualification}
          categorySlug={disqualifiedCategorySlug}
          onGoBack={closePartialDisqualification}
          onContinue={handleDisqualificationContinue}
        />
      )}
    </>
  );
};
